/**
 * Captcha Cloudflare Turnstile pour les appels Supabase Auth.
 *
 * QUELS APPELS SONT CONCERNÉS
 * GoTrue, quand la protection captcha est activée, exige un jeton sur /signup,
 * /recover, /resend, /magiclink, /otp et sur /token avec grant_type=password
 * (internal/api/api.go et isIgnoreCaptchaRoute dans le dépôt supabase/auth).
 * Il en exempte refresh_token, pkce et id_token, ainsi que tout appel porteur
 * d'une clé service_role. Côté Kōken, cela vise donc supabase.auth.signUp et
 * supabase.auth.signInWithPassword : verifyOtp, updateUser, le rafraîchissement
 * de session, le retour OAuth et les edge functions en service_role
 * (admin.createUser, admin.generateLink) ne sont pas touchés.
 *
 * Les cinq formulaires qui font ces appels portent le widget (connexion, deux
 * inscriptions, changement de mot de passe, transfert d'organisation). Restent
 * hors couverture, sans appelant en production au 02/10/2026, et qui seront
 * donc refusés une fois la protection active :
 * - supabase/functions/demo-login (signInWithPassword en clé anon ; le mode
 *   démo invité a été retiré du front en mai 2026) ;
 * - finalizeSocialSignup dans src/services/medianSocialLogin.ts (signUp ; son
 *   seul appelant, SocialLoginCodeModal, n'est monté par aucun écran) ;
 * - l'outillage lancé à la main : scénarios k6 de tests/perf et test Deno de
 *   create-cart-payment-intent, qui se connectent par mot de passe.
 *
 * ORDRE DE DÉPLOIEMENT, À RESPECTER
 * 1. Ce front, avec VITE_TURNSTILE_SITE_KEY renseignée, est publié. Le même
 *    jour, l'inventaire des traceurs (docs/compliance/INVENTAIRE_TRACEURS.md),
 *    la politique cookies et la politique de confidentialité mentionnent
 *    Turnstile : dès cette étape, le navigateur appelle challenges.cloudflare.com.
 * 2. On laisse un à deux jours aux fronts déjà ouverts pour basculer : le
 *    service worker sert d'abord l'ancienne version, sans widget, et ne
 *    recharge la page qu'ensuite. Pendant ce temps on vérifie, protection encore
 *    coupée, que la requête /auth/v1/token porte bien un captcha_token : en
 *    navigation privée cookies refusés (le bandeau pose alors
 *    window.__nightmare, à écarter comme cause de faux positif), et dans
 *    l'application iOS.
 * 3. SEULEMENT ENSUITE, la protection captcha est activée côté Supabase Auth,
 *    avec la clé secrète Turnstile correspondante.
 * Activer avant de publier, et GoTrue refuse toute connexion et toute
 * inscription, le front n'envoyant encore aucun jeton. Un jeton envoyé alors que
 * la protection est désactivée est simplement ignoré : l'étape 1 est sans
 * risque.
 *
 * Sans clé de site, isCaptchaEnabled() vaut false : aucun widget n'est rendu et
 * les appels partent sans jeton, comme avant.
 *
 * INTERRUPTEUR DE SECOURS
 * Si Cloudflare Turnstile tombe une fois la protection active, il suffit de la
 * désactiver côté Supabase Auth : les connexions reprennent sans republier le
 * front. Cela tient parce que useCaptcha débloque le formulaire quand Turnstile
 * échoue, au lieu d'exiger un jeton qui ne viendra pas : tout de suite si le
 * script est refusé, au plus tard après le délai de secours (15 secondes) s'il
 * ne répond pas.
 */

export const TURNSTILE_SITE_KEY: string = (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '').trim();

export function isCaptchaEnabled(): boolean {
  return TURNSTILE_SITE_KEY.length > 0;
}

/**
 * Ajoute captchaToken aux options d'un appel Supabase Auth, uniquement s'il y a
 * un jeton. Sans jeton, les options sont rendues telles quelles : pas de clé
 * captchaToken vide qui ferait échouer GoTrue une fois la protection active.
 */
export function withCaptcha<T extends object>(
  options: T | undefined,
  captchaToken: string | null | undefined,
): T & { captchaToken?: string } {
  const base = (options ?? {}) as T;
  if (!captchaToken) return base;
  return { ...base, captchaToken };
}

/**
 * Vrai si GoTrue a refusé l'appel à cause du captcha : 400 captcha_failed, ou
 * 500 « captcha verification process failed ». Sert à ne pas présenter un refus
 * captcha comme un mot de passe erroné, ce qui égarerait l'utilisateur.
 */
export function isCaptchaError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: unknown; message?: unknown };
  if (e.code === 'captcha_failed') return true;
  return typeof e.message === 'string' && e.message.toLowerCase().includes('captcha');
}
